import { PagedApiResponse, Pagination } from '@oncolens/oncolens-ui/lib/dto/api/ApiResponse';
import { AxiosError } from 'axios';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import client from '../../../utils/httpClient';
import { GuidelineTemplateKeys } from './guideline-template.keys';

export interface GuidelineTemplateDto {
  id: string;
  name: string;
  updatedDate: Date;
  diseaseConditions: GuidelineTemplateConditionDto[];
}

export interface GuidelineTemplateForManipulationDto {
  name: string;
  diseaseConditions: GuidelineTemplateConditionForCreationDto[];
}

export interface GuidelineTemplateConditionForCreationDto {
  cancerTypeId?: string;
  cancerPathologyId?: string;
  cancerLocationId?: string;
  sortOrder?: number;
}

export interface GuidelineTemplateConditionDto {
  cancerTypeId?: string;
  cancerPathologyId?: string;
  cancerLocationId?: string;
  sortOrder?: number;
}

export interface GuidelineTemplateConditionsBatchDto {
  diseaseConditions: GuidelineTemplateConditionDto[];
}

function getGuidelineTemplateById(id: string) {
  return client.get(`/guidelinetemplates/${id}`).then((response) => response.data);
}

function useGuidelineTemplateById(
  id?: string | null | undefined,
  options?: UseQueryOptions<GuidelineTemplateDto>,
) {
  // eslint-disable-next-line no-param-reassign
  id = id === undefined || id === null ? '' : id;
  return useQuery(GuidelineTemplateKeys.detail(id), () => getGuidelineTemplateById(id!), {
    enabled: (id?.length ?? 0) > 0,
    ...options,
  });
}

async function createGuidelineTemplate(
  data: GuidelineTemplateForManipulationDto,
): Promise<GuidelineTemplateDto> {
  return client
    .post<GuidelineTemplateDto>('/guidelinetemplates', data)
    .then((response) => response.data);
}

async function updateGuidelineTemplate(
  id: string,
  data: GuidelineTemplateForManipulationDto,
): Promise<GuidelineTemplateDto> {
  return client
    .put<GuidelineTemplateDto>(`/guidelinetemplates/${id}`, data)
    .then((response) => response.data);
}

function useUpsertGuidelineTemplate(
  id: string | undefined,
  options?: UseMutationOptions<
    GuidelineTemplateDto,
    AxiosError,
    GuidelineTemplateForManipulationDto
  >,
) {
  return useMutation(
    (data) => (id ? updateGuidelineTemplate(id, data) : createGuidelineTemplate(data)),
    options,
  );
}

async function replaceGuidelineTemplateConditionsBatch(
  id: string,
  data: GuidelineTemplateConditionsBatchDto,
) {
  return client.put(`/guidelinetemplates/${id}/diseaseconditions/batch`, data);
}

function getGuidelineTemplates(queryString: string) {
  const query = queryString ? `?${queryString}` : '';
  return client.get(`/guidelinetemplates${query}`).then((response) => {
    const data = response.data as GuidelineTemplateDto[];

    return {
      data,
      pagination: JSON.parse(response.headers['x-pagination']) as Pagination,
    } as PagedApiResponse<GuidelineTemplateDto>;
  });
}

function getAllGuidelineTemplates() {
  return client.get(`/guidelinetemplates/all`).then((response) => {
    const data = response.data as GuidelineTemplateDto[];

    return data;
  });
}

function useAllGuidelineTemplates({
  options,
}: {
  options?: UseQueryOptions<GuidelineTemplateDto[]>;
}) {
  return useQuery(GuidelineTemplateKeys.fullList(), () => getAllGuidelineTemplates(), options);
}

function deleteGuidelineTemplate(id: string) {
  return client.delete<void>(`/guidelinetemplates/${id}`);
}

function useDeleteGuidelineTemplate(id: string, options?: UseMutationOptions) {
  return useMutation(() => deleteGuidelineTemplate(id), options);
}

function useCreateGuidelineTemplate() {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const client = useQueryClient();

  return useMutation(
    (guidelineTemplate: GuidelineTemplateForManipulationDto) =>
      createGuidelineTemplate(guidelineTemplate),
    {
      onSuccess: () => {
        client.invalidateQueries(GuidelineTemplateKeys.all);
      },
    },
  );
}

export {
  createGuidelineTemplate,
  getGuidelineTemplates,
  replaceGuidelineTemplateConditionsBatch,
  updateGuidelineTemplate,
  useAllGuidelineTemplates,
  useCreateGuidelineTemplate,
  useDeleteGuidelineTemplate,
  useGuidelineTemplateById,
  useUpsertGuidelineTemplate,
};
